  import { useState } from "react";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
  import { Link, useNavigate } from "react-router-dom";
  import { Activity } from "lucide-react";
  import { useToast } from "@/hooks/use-toast";

  const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);
    
      try {
        const response = await fetch("/hms/php/login.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
          credentials: "include", // ✅ important to send & receive session cookies
        });
    
        const data = await response.json();
        console.log("Login response:", data);
    
        if (data.status === "success") {
          toast({
            title: "Welcome back!",
            description: `Hello ${data.user.fullname.split(" ")[0]} 👋`,
          });
    
          // Optionally store user details in localStorage for quick access
          localStorage.setItem("user", JSON.stringify(data.user));
    
          navigate("/dashboard");
        } else {
          toast({
            title: "Login failed",
            description: data.message,
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Network error:", error);
        toast({
          title: "Connection Error",
          description: "Unable to connect to the server. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left section with blurred image and logo */}
        <div className="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://media.istockphoto.com/id/1486145188/photo/senior-friends-walking-in-public-park.webp?a=1&b=1&s=612x612&w=0&k=20&c=GKOqVIqUn0YvnoOftQWxiqc-adTuFLJDdvwVga7pSlI=')",
              filter: "blur(px)",
              transform: "scale(1.05)",
            }}
          />
          <div className="relative z-20 flex items-center text-lg font-medium">
            <Link to="/" className="inline-flex items-center gap-2 text-2xl font-bold gradient-health bg-clip-text text-transparent">
              <Activity className="h-8 w-8 text-primary" />
              HealthAI
            </Link>
          </div>
          <div className="relative z-20 mt-auto">
            <blockquote className="space-y-2">
              <p className="text-lg">
                &ldquo;The greatest wealth is health.&rdquo;
              </p>
              <footer className="text-sm">Virgil</footer>
            </blockquote>
          </div>
        </div>

        {/* Right section with login form */}
        <div className="flex items-center justify-center p-4 lg:p-8">
          <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px] animate-scale-in">
            <div className="flex flex-col space-y-2 text-center lg:hidden"> {/* Show logo on small screens */}
              <Link to="/" className="inline-flex items-center justify-center gap-2 text-2xl font-bold gradient-health bg-clip-text text-transparent">
                <Activity className="h-8 w-8 text-primary" />
                HealthAI
              </Link>
            </div>
            <Card className="shadow-xl border-0 rounded-2xl">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold text-center">Welcome back</CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to access your health dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="rounded-xl"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-xl"
                  disabled={isLoading}
                >
                  {isLoading ? "Logging in..." : "Login"}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">Don't have an account? </span>
                <Link to="/register" className="font-medium text-primary hover:underline">
                  Sign up
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    );
  };

  export default Login;
